import { useEffect, useRef } from 'react'
import { HubConnectionBuilder, LogLevel, HttpTransportType } from '@microsoft/signalr'
import { useAuthStore } from '../store/authStore'
import { useInboxNotificationStore } from '../store/inboxNotificationStore'
import { notificationEngineService } from '../services/notificationEngineService'
import { getApiBaseUrl, getRemoteHeartbeatIntervalMs, getSignalRReconnectDelays, delayForTailscaleStagger, withHeartbeatJitter } from '../services/apiConfig'
import { secureStorage } from '../utils/secureStorage'

function hubUrl() {
  return getApiBaseUrl().replace(/\/api\/?$/, '') + '/hubs/notifications'
}

async function loadList() {
  const res = await notificationEngineService.inbox(false, 40, true)
  useInboxNotificationStore.getState().hydrate(res.data || [], Number(res.unreadCount ?? 0))
}

export function useNotificationEngineRealtime() {
  const token = useAuthStore((s) => s.token)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const seen = useRef(new Set<string>())

  useEffect(() => {
    if (!isAuthenticated || !token) return
    let stopped = false
    let connection: ReturnType<typeof HubConnectionBuilder.prototype.build> | null = null
    let poll: ReturnType<typeof setInterval> | null = null

    const onEvent = (raw: any) => {
      const row = useInboxNotificationStore.getState().incoming(raw)
      if (!row || seen.current.has(row.id)) return
      seen.current.add(row.id)
    }

    void (async () => {
      await delayForTailscaleStagger(200)
      if (stopped) return
      try {
        await loadList()
        useInboxNotificationStore.getState().items.forEach((x) => seen.current.add(x.id))
      } catch {
        /* retry via poll */
      }

      const jwt = () => useAuthStore.getState().token || secureStorage.getItem('token') || ''
      connection = new HubConnectionBuilder()
        .withUrl(hubUrl(), {
          accessTokenFactory: jwt,
          withCredentials: false,
          skipNegotiation: true,
          transport: HttpTransportType.WebSockets,
        })
        .withAutomaticReconnect(getSignalRReconnectDelays())
        .configureLogging(LogLevel.None)
        .build()
      connection.on('inboxNotification', onEvent)
      try {
        await connection.start()
      } catch {
        /* poll fallback */
      }

      let pollInflight = false
      poll = setInterval(async () => {
        if (stopped || pollInflight || document.hidden) return
        pollInflight = true
        try {
          const res = await notificationEngineService.inbox(true, 12)
          ;(res.data || []).forEach((raw) => onEvent(raw))
          if (typeof res.unreadCount === 'number') {
            useInboxNotificationStore.setState({ unreadCount: res.unreadCount })
          }
        } catch {
          /* ignore */
        } finally {
          pollInflight = false
        }
      }, withHeartbeatJitter(getRemoteHeartbeatIntervalMs(60_000)))
    })()

    return () => {
      stopped = true
      if (poll) clearInterval(poll)
      connection?.stop().catch(() => undefined)
    }
  }, [isAuthenticated, token])
}
